import {
  ObservaErrorBoundary,
  ObservaProvider,
  useObserva,
  useObservaGlobalHandlers,
} from '@morganisbatman/observa-client-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TRPCClientError, httpBatchLink } from '@trpc/client'
import { StatusBar } from 'expo-status-bar'
import { useEffect, useState } from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { useSession } from './src/auth'
import { AppHeader } from './src/components/AppHeader'
import { ErrorFallback } from './src/components/ErrorFallback'
import { installEditorialFont } from './src/editorial-font'
import { useScreenTracking } from './src/hooks/useScreenTracking'
import { CHECKOUT_CANCELLED, checkoutCancelledPayload } from './src/lib/paywall-events'
import { AccountScreen } from './src/screens/AccountScreen'
import { AuthScreen } from './src/screens/AuthScreen'
import { BandCheckScreen } from './src/screens/BandCheckScreen'
import { PostPaymentScreen } from './src/screens/PostPaymentScreen'
import { trpc } from './src/trpc'

type Screen =
  | 'check'
  | 'auth'
  | 'account'
  | { kind: 'post-payment'; appealId: string }
  | { kind: 'view-result'; bandCheckId: string }

// Read query params on first render and route accordingly:
//   ?paid=1&appealId=X   → post-payment screen (returning from Stripe)
//   ?bandCheck=X         → rehydrate a stored verdict (email "Open your
//                          full report" link)
//   ?promocode=X         → stash in sessionStorage for the next checkout;
//                          PaidAppealCTA reads it and the server bypasses
//                          Stripe when it matches PROMO_CODE env.
// Either way we clear the params via replaceState so a refresh doesn't
// re-trigger the flow. Web-only — native has no URL bar.
export const PROMO_STORAGE_KEY = 'bandcheck.promo.v1'

// Stashed when Stripe bounces the user back to the cancel_url (?cancelled=1).
// initialScreen clears the URL synchronously (before ObservaProvider mounts), so
// the cancelled context is parked here and emitted by CheckoutCancelTracker once
// the observa client is available. Null once consumed.
let pendingCancelled: { appealId: string; bandCheckId: string | null } | null = null

const initialScreen = (): Screen => {
  if (typeof window === 'undefined') return 'check'
  const params = new URLSearchParams(window.location.search)
  // Stash promo code first so it survives the replaceState clear that
  // happens for any of the other routing branches below.
  const promo = params.get('promocode')
  if (promo) {
    try {
      window.sessionStorage.setItem(PROMO_STORAGE_KEY, promo)
    } catch {
      // sessionStorage can be disabled (private browsing on some platforms);
      // silently skip — without a stash the promo just won't apply.
    }
  }
  if (params.get('paid') === '1') {
    const appealId = params.get('appealId')
    if (appealId) {
      window.history.replaceState({}, '', window.location.pathname)
      return { kind: 'post-payment', appealId }
    }
  }
  // Returning from an abandoned Stripe checkout (cancel_url). Park the context
  // for CheckoutCancelTracker, clear the params, and drop the user back on the
  // form — same UX as before, now with a beacon. Must precede the ?bandCheck=
  // branch: the cancel URL also carries bandCheck and would otherwise re-route
  // to the stored-verdict view.
  if (params.get('cancelled') === '1') {
    const appealId = params.get('appealId')
    if (appealId) {
      pendingCancelled = { appealId, bandCheckId: params.get('bandCheck') }
      window.history.replaceState({}, '', window.location.pathname)
      return 'check'
    }
  }
  const bandCheckId = params.get('bandCheck')
  if (bandCheckId) {
    window.history.replaceState({}, '', window.location.pathname)
    return { kind: 'view-result', bandCheckId }
  }
  if (promo) {
    // Promo URL alone (no bandCheck / paid) — clear the param so a refresh
    // doesn't leak the code in the URL bar.
    window.history.replaceState({}, '', window.location.pathname)
  }
  return 'check'
}

// Module-level mirror of the current BetterAuth user id. Filled by
// ObservaInit on every session change, read synchronously by ObservaProvider's
// getUserId on every emit. '0' is observa's "anonymous / not yet known"
// sentinel — rows land now and a future backfill-user hook can link them to
// a real user via sessionId.
let currentUserId = '0'

// observa's session-tracking stores `{ id, startedAt, ... }` JSON at this
// sessionStorage key. Constant duplicated from
// observa-client-react/install-session-tracking.ts so the tRPC fetch wrapper
// can stamp the sessionId on outgoing requests without importing internals.
const OBSERVA_SESSION_STORAGE_KEY = 'observa.session.v1'

// Source-level guard, mirroring server/src/observa-gate.ts: a non-production app
// bundle (local dev, e2e) must never post to the shared prod hub, or its screen
// and paywall events pollute the bandcheckapp conversion funnels — the same
// environment:'development' leak that made the server funnels half test noise.
// Metro statically inlines NODE_ENV, so only the prod web build emits; the
// EXPO_PUBLIC_OBSERVA_FORCE_EMIT escape hatch allows deliberate local hub testing.
const OBSERVA_EMIT_TO_HUB =
  (process.env.NODE_ENV ?? 'development') === 'production' ||
  process.env.EXPO_PUBLIC_OBSERVA_FORCE_EMIT === '1'

const readObservaSessionId = (): string | null => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null
  const raw = window.sessionStorage.getItem(OBSERVA_SESSION_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { id?: unknown }
    return typeof parsed.id === 'string' ? parsed.id : null
  } catch {
    return null
  }
}

const ObservaInit = () => {
  const session = useSession()
  const userId = session.data?.user?.id ?? '0'
  useEffect(() => {
    currentUserId = userId
  }, [userId])
  useObservaGlobalHandlers({
    captureSessions: true,
    capturePageViews: true,
    captureConsole: true,
  })
  return null
}

// Emits checkout.cancelled once on mount if the user just returned from an
// abandoned Stripe checkout (parked by initialScreen). Lives inside
// ObservaProvider so it can reach the client; fire-and-forget like the rest.
const CheckoutCancelTracker = () => {
  const observa = useObserva()
  useEffect(() => {
    if (!pendingCancelled) return
    const { appealId, bandCheckId } = pendingCancelled
    pendingCancelled = null
    void observa
      .event({
        eventType: CHECKOUT_CANCELLED,
        payload: checkoutCancelledPayload(appealId, bandCheckId),
      })
      .catch(() => {})
  }, [observa])
  return null
}

// Stable screen label for dwell tracking. This app routes by state, not URL,
// so the SDK's history-based page-view capture can't see screen changes —
// useScreenDwell keyed on this label fills that gap (time-on-page for every
// screen, including the post-purchase fulfilment flow).
const screenLabel = (screen: Screen) => (typeof screen === 'string' ? screen : screen.kind)

// Emits screen.viewed on enter + screen.dwell on leave for every screen change.
// Must live inside ObservaProvider to reach the client. Rendered with the
// current screen label; when it changes, the hook records the navigation and
// flushes the previous screen's dwell time.
const ScreenTracker = ({ screen }: { screen: Screen }) => {
  useScreenTracking(screenLabel(screen))
  return null
}

export const App = () => {
  const [screen, setScreen] = useState<Screen>(initialScreen)
  // Inject the editorial serif (@font-face) once on web; native no-ops.
  useEffect(() => {
    installEditorialFont()
  }, [])
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          // Client errors (4xx) won't fix themselves — don't retry them; retry
          // transient failures (5xx / network) once before surfacing.
          queries: {
            retry: (failureCount, error) => {
              const status =
                error instanceof TRPCClientError
                  ? (error.data?.httpStatus as number | undefined)
                  : undefined
              if (typeof status === 'number' && status >= 400 && status < 500) return false
              return failureCount < 1
            },
          },
          mutations: { retry: false },
        },
      }),
  )
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          // .trim() guards against stray whitespace in CI env values — a
          // trailing newline produces `/trpc /bandCheck.run` 404s.
          url: process.env.EXPO_PUBLIC_API_URL?.trim() ?? 'http://localhost:3000/trpc',
          // Stamp every outgoing tRPC batch with the frontend's observa
          // sessionId and a fresh correlationId. The Fastify resolveContext
          // hook reads both headers and threads them into AsyncLocalStorage,
          // so server-side observa.event() calls inside the request auto-tag
          // with the same sessionId the frontend used — that's the join key
          // between frontend page-view rows and backend band.check.completed
          // rows for the same user action.
          fetch(url, options) {
            const headers = new Headers(options?.headers)
            const sessionId = readObservaSessionId()
            if (sessionId) headers.set('x-observa-session-id', sessionId)
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
              headers.set('x-correlation-id', crypto.randomUUID())
            }
            return fetch(url, { ...options, headers, credentials: 'include' })
          },
        }),
      ],
    }),
  )

  return (
    <ObservaProvider
      config={{
        // Must equal the hub project slug — strict onboarding + key↔project
        // binding reject a mismatch (was 'bandcheck-web' under the old mounted
        // setup; the hub registers this surface as 'bandcheckapp').
        appId: 'bandcheckapp',
        // Set by CI at build time (see .github/workflows/source-maps.yml).
        // MUST match the APP_VERSION used by scripts/upload-source-maps.ts on
        // the same build, otherwise the hub can't symbolicate prod stack traces
        // against the uploaded maps. '0.0.0' is the local-dev fallback — unbuilt
        // source has no SHA.
        appVersion: process.env.EXPO_PUBLIC_APP_VERSION ?? '0.0.0',
        environment: process.env.NODE_ENV ?? 'development',
        // Gated (OBSERVA_EMIT_TO_HUB): non-production bundles emit to the dead
        // localhost endpoint instead of the real hub, so a dev/e2e run that
        // happens to have EXPO_PUBLIC_OBSERVA_URL pointed at the prod hub can't
        // pollute it. We override only the ENDPOINT (not the key) so the SDK
        // still ATTEMPTS each POST — the telemetry e2e specs assert the outgoing
        // `_observability/trpc` request fires; the dead host just refuses it and
        // the SDK swallows the failure.
        endpoint: OBSERVA_EMIT_TO_HUB
          ? (process.env.EXPO_PUBLIC_OBSERVA_URL?.trim() ??
            'http://localhost:4000/_observability/trpc')
          : 'http://localhost:4000/_observability/trpc',
        // Public ingest key (opk_) for the 'bandcheckapp' hub project, sent as a
        // Bearer token. Public keys are safe to ship in the browser bundle; the
        // hub's registered-origins allowlist is what actually gates ingest.
        key: process.env.EXPO_PUBLIC_OBSERVA_KEY,
        // Called on every frontend emit. ObservaInit (below) mirrors the
        // BetterAuth session userId into currentUserId, so this returns the
        // real id once signed in and '0' for anonymous visitors. Combined
        // with the tRPC fetch wrapper stamping x-observa-session-id, this
        // closes the join: anonymous frontend rows share a sessionId with
        // the authed backend rows that follow, so a userId backfill keyed on
        // sessionId can link them after sign-up.
        getUserId: () => currentUserId,
      }}
    >
      <ObservaErrorBoundary fallback={(_error, reset) => <ErrorFallback onReset={reset} />}>
        <ObservaInit />
        <CheckoutCancelTracker />
        <ScreenTracker screen={screen} />
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <View style={styles.container}>
              <AppHeader
                onHome={() => setScreen('check')}
                onAuth={() => setScreen('auth')}
                onAccount={() => setScreen('account')}
              />
              {screen === 'check' ? (
                <BandCheckScreen />
              ) : screen === 'auth' ? (
                <ScrollView contentContainerStyle={styles.padded}>
                  <AuthScreen
                    onAuthed={() => setScreen('account')}
                    onCancel={() => setScreen('check')}
                  />
                </ScrollView>
              ) : screen === 'account' ? (
                <AccountScreen onSignedOut={() => setScreen('check')} />
              ) : screen.kind === 'post-payment' ? (
                <PostPaymentScreen
                  appealId={screen.appealId}
                  onDismiss={() => setScreen('check')}
                />
              ) : (
                <BandCheckScreen initialBandCheckId={screen.bandCheckId} />
              )}
              <StatusBar style="auto" />
            </View>
          </QueryClientProvider>
        </trpc.Provider>
      </ObservaErrorBoundary>
    </ObservaProvider>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'white' },
  padded: { padding: 24, alignItems: 'center' },
})
